import React from 'react'
import axios from 'axios'
import Pusher from 'pusher-js'
import { hydrate } from 'react-dom'
import allSettled from '@ungap/promise-all-settled'
import 'url-search-params-polyfill'
import facebookAuth from './actions/facebook-auth'
import {
    Alert,
    SwConfirm,
} from '../components/shared/sweet-alert'
import {
    getUrlParameter,
    setEcho,
    isWebView,
} from './actions'
import getLoggedUser from './actions/get-logged-user'
import setLoggedUser from './actions/set-logged-user'
import sendRefreshToken from './actions/send-refresh-token'
import logout from './actions/logout'
import store, { getItemFromLocalStorage } from './store'
import { setLoginError } from './reducers/auth'
import {
    clearApiErrorAction,
    setApiErrorAction,
} from './actions/errors'
import i18n from '../locales/i18n'

import tokenQueryParams, { isAccessToken } from '../components/auth/token-query-params'
import { updateFormValueAction } from './actions/auth'
import { setPhotoToUpload } from './actions/photo-upload'
import {
    registerLocale,
    setDefaultLocale,
} from 'react-datepicker'
import {
    cs,
    enGB,
    de,
    pl,
} from 'date-fns/locale'

const canSendRefresh = (err, tokens) => !!(err.response && err.response.status === 401 && tokens && tokens.refresh_token)

const trySendRefresh = (err, tokens) => {
    if (canSendRefresh(err, tokens)) {
        return sendRefreshToken(tokens.refresh_token).then(response => {
            if (response && response.access_token) {
                /** @TODO - not reload, but set new request */
                window.location.reload()
                return null
                // err.response.config.headers.Authorization = `Bearer ${accessToken}`
                // window.axios(err.response.config).then(response => {
                //     return Promise.resolve(response)
                // })
            } else {
                // return logout()
            }
        })
    }

    // return logout()
}

const preflight = () => {
    let userLang = window.location.pathname.substring(0, 4).replace('/', '').replace('/', '')

    let momentLanguage = userLang

    switch (userLang) {
        case 'en':
            momentLanguage = 'en-gb'
            break
    }

    moment.locale(momentLanguage)

    registerLocale('cs', cs)
    registerLocale('en-gb', enGB)
    registerLocale('pl', pl)
    registerLocale('de', de)
    setDefaultLocale(momentLanguage)

    /**
     * We'll load jQuery and the Bootstrap jQuery plugin which provides support
     * for JavaScript based Bootstrap features such as modals and tabs. This
     * code may be modified to fit the specific needs of your application.
     */

    require('bootstrap')

    if (!Promise.allSettled) {
        Promise.allSettled = allSettled
    }

    window.axios = axios

    window.axios.defaults.headers.common = {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'App-Device': `web-${window?.APP_CONFIG?.REACT_APP_SYSTEM}`,
        'Accept-Language': userLang,
    }

    let tokens = store.getState().auth.tokens || getItemFromLocalStorage('sportnect.tokens')

    window.axios.interceptors.request.use(request => {
        clearApiErrorAction()
        return request
    })

    window.axios.interceptors.response.use(null, err => {
        tokens = store.getState().auth.tokens || getItemFromLocalStorage('sportnect.tokens')

        // Check if request is logging to system
        const isLoginRequest = (err?.request?.responseURL || '').includes('oauth/token')
        const isCredentialsError = !!setLoginError({}, err?.response).error || isLoginRequest

        setApiErrorAction(err)

        if (err?.response?.status === 301 && err?.response?.data?.redirect) {
            window.location.href = window.location.href.replace(err?.response?.data?.oldSlug, err?.response?.data?.newSlug)
        }

        if (err?.response?.status === 429) {
            return window.swalert('Příliš mnoho požadavků, opakujte prosím akci za chvíli')
        }

        if (err?.response?.status === 500) {
            window.swalert(i18n.t('serverErrorMessage'))
        }

        // User is unlogged and refresh token is not valid -> logout
        if (err?.response?.status === 401 && err?.response?.data?.error === 'invalid_request') {
            return logout()
        }

        // User is unlogged and refresh token is not exists -> logout
        if (err?.response?.status === 401 && (!tokens || !tokens?.refresh_token)) {
            return logout()
        }

        // User have fatal bad access token -> logout
        const errorMessage =  err?.response?.data?.error || ''
        const isUnauthorized = err?.response?.statusText?.toLowerCase() === 'unauthorized'
        const isUnauthenticated = typeof errorMessage === 'string' ? errorMessage.includes('Unauthenticated') : false

        if (err?.response?.status === 401 && (isUnauthenticated || isUnauthorized) && !isCredentialsError) {
            return logout()
        }

        if (canSendRefresh(err, tokens)) {
            return trySendRefresh(err, tokens)
        }

        return Promise.reject(err.response)
    })

    // If is set access_token to URL, set it
    if (isAccessToken) {
        store.dispatch({
            type: 'TOGGLE_FETCHING_AUTHED_USER',
            inFlight: true,
        })
        setLoggedUser(tokenQueryParams(), getUrlParameter('callback') || false, true)
            .then(() => (
                store.dispatch({
                    type: 'TOGGLE_FETCHING_AUTHED_USER',
                    inFlight: false,
                })
            ))
    } else {
        // OR...
        // if we are logged in set authoriation token for all requests
        if (tokens) {
            store.dispatch({
                type: 'TOGGLE_FETCHING_AUTHED_USER',
                inFlight: true,
            })

            window.axios.defaults.headers.common.Authorization = `Bearer ${tokens.access_token}`

            getLoggedUser()
                .then(() => store.dispatch({
                    type: 'TOGGLE_FETCHING_AUTHED_USER',
                    inFlight: false,
                }))
                .catch(err => trySendRefresh(err))
        }
    }

    if (!isWebView()) {
        window.Pusher = Pusher
        setEcho(tokens)
    }

    /**
     * Call better alerts
     * USAGE = anywhere in code window.swalert('FOO')  OR  window.swalert('FOO', 'success')
     *
     * @param {string} text
     * @param {string|null} type    - types defined in inherit doc - primary|info|success|warning|danger
     * @param {function|null} onConfirm
     * @inheritDoc http://djorg83.github.io/react-bootstrap-sweetalert/
     */
    window.swalert = (text, type = 'danger', onConfirm = null) => hydrate(<Alert title={text} type={type} onConfirm={onConfirm} />, document.getElementById('alert-container'))

    /**
     * Call better confirms
     * USAGE = anywhere in code window.swconfirm({ --params-- })
     * PARAMS -
     *
     * {
     *      title: 'Opravdu chcete smazat tuto položku?',   // required - string
     *      onConfirm: () => deleteGroup(group.slug),       // required - promise
     *      confirmBtnText: 'Ano',                          // optional - string
     *      cancelBtnText: 'Ne',                            // optional - string
     *      afterConfirm: () => loadGroups(),               // optional - promise
     *      type: 'danger',                                 // optional - string
     *  }
     *
     * @param {Object} props
     * @inheritDoc http://djorg83.github.io/react-bootstrap-sweetalert/
     */
    window.swconfirm = props => hydrate(<SwConfirm {...props} />, document.getElementById('alert-container'))

    /**
     * Update email and password in store for login form (used in Android webview)
     * @param {string} email
     * @param {string} pass
     */
    window.fillLoginForm = (email, pass) => {
        updateFormValueAction('loginForm', 'email', email)
        updateFormValueAction('loginForm', 'password', pass)
    }

    /**
     * Login to webview with FB token
     * @param {string} fbToken
     * @returns {Promise}
     */
    window.loginWithFBToken = fbToken => facebookAuth({ accessToken: fbToken })

    /**
     * Set photo to upload in store for UploadPhotoWebview component
     * @param {string} imageBase64
     * @param {string} rootType
     * @param {string} rootId
     * @returns {void}
     */
    window.uploadPhoto = (imageBase64, rootType='user', rootId) => {
        const storeCopy = store.getState()
        setPhotoToUpload(imageBase64, rootType, rootId || storeCopy.auth.user?.id)
    }
}

export default preflight
